import React from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import IconButton, {
  IconButtonSizes,
  IconButtonTypes,
} from '@lib/components/IconButton/IconButton';
import styles from '@lib/components/Table/components/TableBody/TableBody.module.scss';
import CellWithActions from '@lib/components/Table/components/TableBody/CellWithActions/CellWithActions';
import {
  BaseItem,
  CellContentAlign,
  Column,
  TableTreeRowState,
} from '@lib/components/Table/types';
import CellContent from '@lib/components/Table/components/TableBody/CellContent';

type Props<T extends BaseItem> = {
  column: Column<T>;
  rowItem: T;
  colSpan?: number;
  isDisabled?: boolean;
  isFirstColumn?: boolean;
  onToggleTreeRow?: () => void;
  treeState?: TableTreeRowState;
};

export default function TableBodyCell<T extends BaseItem>(props: Props<T>) {
  const {
    column,
    rowItem,
    colSpan,
    isDisabled,
    isFirstColumn,
    onToggleTreeRow,
    treeState,
  } = props;
  const { t } = useTranslation();

  const {
    bordered,
    maxWidth,
    minWidth,
    width,
    actions,
    contentAlign,
    prohibitDisabled,
    cellClassName,
    noWrap,
    onCellClick,
  } = column;
  const isMerged = !!colSpan && colSpan > 1;
  const cellStyle = {
    width: isMerged ? undefined : width,
    minWidth: isMerged ? undefined : minWidth,
    maxWidth: isMerged ? undefined : maxWidth,
    whiteSpace: noWrap ? 'nowrap' : undefined,
  };
  const cellElClassName = classNames(cellClassName, {
    [styles.borderedCell]: bordered,
  });
  const cellOnClick = onCellClick?.(rowItem);
  const cellContentClassName = classNames(styles.tdContent, {
    [styles.tdContentCenter]: contentAlign === CellContentAlign.Center,
    [styles.tdContentEnd]: contentAlign === CellContentAlign.End,
    [styles.tdContentDisabled]: isDisabled && !prohibitDisabled,
    [styles.tdContentClickable]: !!cellOnClick,
  });
  const handleCellContentClick = cellOnClick
    ? (event: React.MouseEvent) => {
        event.stopPropagation();
        cellOnClick();
      }
    : undefined;
  const handleCellContentKeyDown = cellOnClick
    ? (event: React.KeyboardEvent) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        event.stopPropagation();
        cellOnClick();
      }
    : undefined;

  const treeCellState = isFirstColumn ? treeState : undefined;
  // A flat search result renders its `path` in place of the tree affordance,
  // so it never gets an expand toggle — even if the row reports children, they
  // are not part of the flat match list the user is looking at.
  const hasTreeToggle =
    !!treeCellState?.hasChildren && treeCellState.path === undefined;

  const renderContent = () => {
    if (treeCellState?.path !== undefined) {
      return <span className={styles.treePath}>{treeCellState.path}</span>;
    }

    return <CellContent column={column} item={rowItem} />;
  };

  return (
    <td
      className={cellElClassName}
      style={cellStyle}
      colSpan={isMerged ? colSpan : undefined}
      data-test-id={column.id ? `table__body-cell-${column.id}` : undefined}
    >
      {actions ? (
        <CellWithActions column={column} item={rowItem} />
      ) : (
        <div
          className={cellContentClassName}
          style={
            treeCellState
              ? ({
                  '--tree-depth': treeCellState.depth,
                } as React.CSSProperties)
              : undefined
          }
          {...(cellOnClick
            ? {
                onClick: handleCellContentClick,
                onKeyDown: handleCellContentKeyDown,
                role: 'button' as const,
                tabIndex: 0,
              }
            : {})}
        >
          {treeCellState && (
            <span className={styles.treeToggle}>
              {hasTreeToggle ? (
                <IconButton
                  aria-label={
                    treeCellState.isExpanded ? t('collapse') : t('expand')
                  }
                  data-test-id={`table__tree-toggle-${rowItem.id}`}
                  disabled={treeCellState.isLoadingChildren}
                  icon={
                    treeCellState.isExpanded ? 'expand_more' : 'chevron_right'
                  }
                  isLoading={treeCellState.isLoadingChildren}
                  size={IconButtonSizes.ExtraSmall}
                  type={IconButtonTypes.QuaternaryFilled}
                  onClick={(event) => {
                    event.stopPropagation();
                    onToggleTreeRow?.();
                  }}
                />
              ) : (
                <span className={styles.treeTogglePlaceholder} />
              )}
            </span>
          )}
          {renderContent()}
        </div>
      )}
    </td>
  );
}
